import { defineStore } from 'pinia';
import { PayoutOrderListItem, PayoutOrderListItemResponse } from '@/models/payout-order/payout-order-list.model';
import { api } from '@/services/http.service';
import { ResponseWithHeaders } from '@/models/common/response-with-header.model';
import { DEFAULT_LIMIT, DEFAULT_MAX_LIMIT, DEFAULT_PAGE_NUMBER } from '@/constants/common/constants';
import { ResponseHeaders } from '@/models/common/headers.model';
import { PayoutOrderListFilter } from '@/models/payout-order/payout-order-list-filter.model';

interface PayoutOrderListState {
  isPayoutOrderListLoading: boolean;
  payoutOrderList: PayoutOrderListItem[];
  limit: number;
  currentPage: number;
}

export const usePayoutOrderListStore = defineStore('PayoutOrderListStore', {
  state: (): PayoutOrderListState => ({
    isPayoutOrderListLoading: false,
    payoutOrderList: [],
    limit: DEFAULT_LIMIT,
    currentPage: DEFAULT_PAGE_NUMBER,
  }),
  getters: {
    payoutOrderListCount(state: PayoutOrderListState): number {
      return state.payoutOrderList.length;
    },
    paginatedPayoutOrderList(state: PayoutOrderListState): PayoutOrderListItem[] {
      const offset = (state.currentPage - 1) * state.limit;

      return state.payoutOrderList.slice(offset, offset + state.limit);
    },
  },
  actions: {
    setPayoutOrderListLoading(isLoading: boolean): void {
      this.isPayoutOrderListLoading = isLoading;
    },
    setPayoutOrderList(payoutOrderList: PayoutOrderListItem[]): void {
      this.payoutOrderList = payoutOrderList;
    },
    async getPayoutOrderListByWhitelabel(
      whitelabelSlug: string,
      filterList: PayoutOrderListFilter = {},
    ): Promise<PayoutOrderListItemResponse[]> {
      const result: PayoutOrderListItemResponse[] = [];

      let nextPageToken: string | null = null;

      do {
        const requestParams = {
          filterList,
          limit: DEFAULT_MAX_LIMIT,
          ...(nextPageToken && { nextPageToken }),
        };

        const {
          data,
          headers,
        }: ResponseWithHeaders<PayoutOrderListItemResponse[]> =
          await api.payoutOrders.getPayoutOrderListRequest.perform(
            whitelabelSlug,
            requestParams,
          );

        result.push(...data);

        const newNextPageToken =
          headers[ResponseHeaders.NEXT_PAGE_TOKEN_HEADER] ?? null;

        if (!newNextPageToken || newNextPageToken === nextPageToken) {
          break;
        }

        nextPageToken = newNextPageToken;
      } while (nextPageToken);

      return result;
    },
  },
});
